/**
 * AnimatedStatementBanner — Web Component
 *
 * Cycles through [data-statement] items with a vertical slide animation.
 * Pauses on hover (accessibility). Respects prefers-reduced-motion.
 * Reinitializes on Shopify section:load (theme editor support).
 */
class AnimatedStatementBanner extends HTMLElement {
  constructor() {
    super();
    /** @type {number} */
    this.currentIndex = 0;
    /** @type {HTMLElement[]} */
    this.items = [];
    /** @type {ReturnType<typeof setInterval> | null} */
    this.timer = null;
    /** @type {number} */
    this.speed = 4000;
    this._onSectionLoad = this._onSectionLoad.bind(this);
    this._onMouseEnter = this._pause.bind(this);
    this._onMouseLeave = this._resume.bind(this);
  }

  connectedCallback() {
    this._init();
    document.addEventListener('shopify:section:load', this._onSectionLoad);
    this.addEventListener('mouseenter', this._onMouseEnter);
    this.addEventListener('mouseleave', this._onMouseLeave);
  }

  disconnectedCallback() {
    this._stop();
    document.removeEventListener('shopify:section:load', this._onSectionLoad);
    this.removeEventListener('mouseenter', this._onMouseEnter);
    this.removeEventListener('mouseleave', this._onMouseLeave);
  }

  /**
   * @param {Event} event
   */
  _onSectionLoad(event) {
    const target = event.target;
    if (target instanceof Element && target.contains(this)) {
      this._stop();
      this._init();
    }
  }

  _init() {
    this.items = Array.from(this.querySelectorAll('[data-statement]'));
    if (this.items.length < 2) return;

    this.speed = (parseInt(this.dataset.speed ?? '4', 10) || 4) * 1000;
    this.currentIndex = 0;

    // Ensure only the first item is active on init
    this.items.forEach((item, index) => {
      item.classList.toggle('is-active', index === 0);
      item.classList.remove('is-leaving');
      item.style.transition = '';
    });

    // Skip cycling animation for users who prefer reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.timer = setInterval(() => this._advance(), this.speed);
  }

  _stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  _pause() {
    this._stop();
  }

  _resume() {
    if (this.items.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.timer = setInterval(() => this._advance(), this.speed);
  }

  _advance() {
    const leaving = this.items[this.currentIndex];
    this.currentIndex = (this.currentIndex + 1) % this.items.length;
    const entering = this.items[this.currentIndex];

    if (!leaving || !entering) return;

    // Slide current item out upward
    leaving.classList.remove('is-active');
    leaving.classList.add('is-leaving');

    // Slide next item in from below
    entering.classList.add('is-active');

    // After the leaving transition ends, reset the item below the viewport
    // without a visible animation so it's ready for the next cycle.
    leaving.addEventListener(
      'transitionend',
      () => {
        leaving.classList.remove('is-leaving');
        // Temporarily disable transition to avoid a snap-back flash
        leaving.style.transition = 'none';
        // Force layout recalculation before re-enabling transition
        void leaving.offsetWidth;
        leaving.style.transition = '';
      },
      { once: true }
    );
  }
}

customElements.define('animated-statement-banner', AnimatedStatementBanner);
